import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  format,
  subMonths,
  subYears,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { DateRangePicker } from "react-date-range";

import {
  FormControl,
  Select,
  InputLabel,
  ListItemText,
  OutlinedInput,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  InputAdornment,
  Popper,
  ClickAwayListener,
  Fade,
  FormHelperText,
} from "@mui/material";
import {
  Event as DateRangeIcon,
  Campaign as CampaignTypeIcon,
  ManageSearch,
} from "@mui/icons-material";

import { ImCross } from "react-icons/im";
import { FaCheck } from "react-icons/fa";
import { FiRefreshCcw } from "react-icons/fi";
import {
  AlertCircle,
  Calendar,
  Layers,
  Pencil,
  PieChart,
  Trash2,
  Wallet,
} from "lucide-react";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  apiRequestBodyBuilder,
  displayNumericValue,
  getCurrencySymbol,
} from "../pages/config/tableHelper";

const campaignTypeOptions = [
  { title: "Search", value: "SEARCH" },
  { title: "Shopping", value: "SHOPPING" },
  { title: "Display", value: "DISPLAY" },
  { title: "Video", value: "VIDEO" },
  { title: "Performance Max", value: "PERFORMANCE_MAX" },
];

const ENGINES = [
  { title: "Google Ads", value: "google_ads" },
  { title: "Bing Ads", value: "Bing_ads" },
];

const budgetTypeOptions = [
  {
    title: "Overall Budget",
    description: "Set budget for all campaign types combined",
    value: "overall",
    icon: "📊",
  },
  {
    title: "Campaign Type Wise",
    description: "Set budget for specific campaign types",
    value: "campaign_type",
    icon: "🎯",
  },
  {
    title: "Group Wise",
    description: "Set budget for specific group",
    value: "group_wise",
    icon: "👨‍👦‍👦",
  },
];

export default function SettingsPage() {
  const { id: routeProjectId } = useParams();
  const [groups, setGroups] = useState([]);
  const [projectId, setProjectId] = useState(
    routeProjectId || localStorage.getItem("project_id") || "",
  );

  const [budgetName, setBudgetName] = useState("");
  const [totalMonthlyBudget, setTotalMonthlyBudget] = useState("");
  const [campaignType, setCampaignType] = useState("");
  const [selectedEngine, setSelectedEngine] = useState("google_ads");
  const [savingBudget, setSavingBudget] = useState(false);
  const [budgetCurrency, setBudgetCurrency] = useState(getCurrencySymbol());

  const [timeFilter, setTimeFilter] = useState("all_time");
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerAnchorEl, setDatePickerAnchorEl] = useState(null);
  /** Date range for fetching / filtering the monthly budget list ( driven by timeFilter ). */
  const [budgetDateSelection, setBudgetDateSelection] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  /** Date range for creating a new monthly budget (defaults to current calendar month). */
  const [budgetCreationDateSelection, setBudgetCreationDateSelection] =
    useState(() => {
      const now = new Date();
      return [
        {
          startDate: startOfMonth(now),
          endDate: endOfMonth(now),
          key: "selection",
        },
      ];
    });
  const budgetPeriodFieldRef = useRef(null);

  const [selectedGroup, setSelectedGroup] = useState("");
  const [random, setRandom] = useState(Math.random());
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [selectedBudgetType, setSelectedBudgetType] = useState("");
  const [budgetSourceType, setBudgetSourceType] = useState("new");
  const [seperateBudgeType, setSeperateBudgetType] = useState("none");
  const [seperateBudget, setSeperateBudget] = useState(0);
  const [percentage, setPercentage] = useState("");
  const [selectedExistingBudget, setSelectedExistingBudget] = useState("");
  const [calculatedBudget, setCalculatedBudget] = useState(null);
  const [filteredBudgetData, setFilteredBudgetData] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetError, setBudgetError] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [editedBudgetValue, setEditedBudgetValue] = useState("");
  const [editedBudgetName, setEditedBudgetName] = useState("");
  const [editedGoogleSplit, setEditedGoogleSplit] = useState("");
  const [editedDateSelection, setEditedDateSelection] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const [showEditDatePicker, setShowEditDatePicker] = useState(false);
  const [editDatePickerAnchorEl, setEditDatePickerAnchorEl] = useState(null);

  const [connectedPlatforms, setConnectedPlatforms] = useState({
    google: false,
    microsoft: false,
  });
  function areBothPlatformAvailable() {
    return connectedPlatforms.google && connectedPlatforms.microsoft;
  }

  const iconClasses = "mr-1 text-gray-500";
  const textFieldClasses = "w-full rounded-lg";
  const saveButtonBaseClasses =
    "font-medium normal-case text-[0.9375rem] py-[10px] px-6 rounded-lg min-w-[100px]";

  useEffect(() => {
    const storedString = localStorage.getItem("platform_connected");
    let platforms = {};
    try {
      platforms = storedString ? JSON.parse(storedString) : {};
    } catch (e) {
      console.error("Corrupt platform data in local storage", e);
      platforms = {};
    }

    setConnectedPlatforms({
      google: !!platforms?.google_ads,
      microsoft: !!platforms?.microsoft_ads,
    });
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      let requestBody = apiRequestBodyBuilder(budgetDateSelection);

      const responseInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );

      if (!responseInfo.ok) {
        throw new Error(
          `Failed to fetch group info. Status: ${responseInfo.status}`,
        );
      }
      const groupType = await responseInfo.json();

      const typeForQuery =
        groupType?.type === "keyword"
          ? "keywords"
          : (groupType?.type ?? "campaign");

      requestBody.type = typeForQuery;
      const response = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch groups. Status: ${response.status}`);
      }
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (err) {
      console.error("An error occurred while fetching groups:", err);
      setGroups([]);
    }
  }, [budgetDateSelection]);

  const deleteMonthlyBudget = async (item) => {
    let url = "https://api.confidanto.com/monthly-budget/delete";
    let reqBody = {
      id: item.id,
      projectId: item.project_id,
      customer_id: item.customer_id,
      email: item.email,
      start_date: format(new Date(item.start_date), "yyyy-MM-dd"),
      end_date: format(new Date(item.end_date), "yyyy-MM-dd"),
      group_id: item.group_id ? item.group_id : null,
      campaignType: item.campaign_type ? item.campaign_type : null,
    };

    let confirm = window.confirm(
      "Are you sure you want to delete this budget?",
    );

    if (confirm) {
      await axios
        .post(url, reqBody)
        .then((res) => {
          console.log(res.data.message);
          alert(res.data.message);
          fetchMonthlyBudget();
        })
        .catch((err) => {
          console.log(err);
          alert(err);
        });
    }
  };

  const closeAll = () => {
    setShowBudgetForm(false);
    setSelectedBudgetType("");
    setCampaignType("");
    setSelectedGroup("");
    setBudgetName("");
    setBudgetSourceType("new");
    setTotalMonthlyBudget("");
    setSelectedExistingBudget("");
    setPercentage("");
    setSeperateBudgetType("none");
    setSeperateBudget(0);
  };

  const handleSaveMonthlyBudget = async () => {
    if (!projectId) {
      alert("Project ID is missing. Cannot save budget.");
      return;
    }

    const customerIdFromStorage = localStorage.getItem("customer_id");
    const emailFromStorage = localStorage.getItem("email");

    if (!customerIdFromStorage || !emailFromStorage) {
      alert(
        "Customer ID or Email is missing from storage. Please ensure you are logged in correctly.",
      );
      return;
    }

    const { startDate, endDate } = budgetCreationDateSelection[0];

    if (!startDate || !endDate) {
      alert("Invalid date selected for budget period.");
      return;
    }

    const formattedStartDate = format(startDate, "yyyy-MM-dd");
    const formattedEndDate = format(endDate, "yyyy-MM-dd");

    let budgetToSave;
    if (budgetSourceType === "new" || selectedBudgetType === "overall") {
      if (!totalMonthlyBudget) {
        alert("Please fill in the total monthly budget.");
        return;
      }
      budgetToSave = parseFloat(totalMonthlyBudget);
    } else {
      if (!calculatedBudget) {
        alert("Please select a percentage and an existing budget.");
        return;
      }
      budgetToSave = parseFloat(calculatedBudget);
    }

    let payload;
    if (selectedBudgetType === "campaign_type") {
      if (!campaignType) {
        alert("Please fill in all budget fields.");
        return;
      }
      payload = {
        projectId: Number(projectId),
        campaignType: campaignType,
        customer_id: customerIdFromStorage,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        email: emailFromStorage,
        budget_name: budgetName,
        total_budget: budgetToSave,
        percentage: percentage,
        existing_budget_id: selectedExistingBudget,
        platform: selectedEngine,
      };
    } else if (selectedBudgetType === "group_wise") {
      if (!selectedGroup) {
        alert("Please fill in all budget fields.");
        return;
      }

      const groupId = groups.find((item) => item.group_name === selectedGroup);
      if (!groupId) {
        alert("Invalid Group");
        return;
      }
      payload = {
        projectId: Number(projectId),
        group_id: groupId.group_id,
        customer_id: customerIdFromStorage,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        total_budget: budgetToSave,
        email: emailFromStorage,
        budget_name: budgetName,
        percentage: percentage,
        existing_budget_id: selectedExistingBudget,
      };
    } else {
      payload = {
        projectId: Number(projectId),
        customer_id: customerIdFromStorage,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        total_budget: budgetToSave,
        email: emailFromStorage,
        budget_name: budgetName,
      };

      if (seperateBudgeType !== "none" && seperateBudget === "") {
        alert("Please select a Separate Budget");
        return;
      }

      if (seperateBudgeType !== "none") {
        let googleAdsBudget =
          seperateBudgeType === "google_ads"
            ? Number(seperateBudget)
            : 100 - Number(seperateBudget);
        let bingAdsBudget =
          seperateBudgeType === "bing_ads"
            ? Number(seperateBudget)
            : 100 - Number(seperateBudget);
        payload = {
          ...payload,
          is_seperate_budget: true,
          google_ads_budget: googleAdsBudget,
          bing_ads_budget: bingAdsBudget,
        };
      } else {
        let googleDefault = 50;
        let bingDefault = 50;

        if (connectedPlatforms.google && !connectedPlatforms.microsoft) {
          googleDefault = 100;
          bingDefault = 0;
        } else if (!connectedPlatforms.google && connectedPlatforms.microsoft) {
          googleDefault = 0;
          bingDefault = 100;
        }

        payload = {
          ...payload,
          is_seperate_budget: false,
          google_ads_budget: googleDefault,
          bing_ads_budget: bingDefault,
        };
      }
    }

    setSavingBudget(true);
    try {
      const response = await fetch(
        "https://api.confidanto.com/monthly-budget",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: "Failed to parse error response from server.",
        }));
        console.error("API Error:", response.status, errorData);
        alert(
          `Failed to save monthly budget: ${
            errorData.message || response.statusText
          }`,
        );
        throw new Error(`API request failed with status ${response.status}`);
      }
      const result = await response.json();
      if (selectedBudgetType === "group_wise") {
        alert("Monthly budget saved successfully for Group!");
      } else if (selectedBudgetType === "campaign_type") {
        alert("Monthly budget saved successfully for Campaign Type!");
      } else {
        alert("Overall Monthly budget saved successfully!");
      }

      const now = new Date();
      setBudgetCreationDateSelection([
        {
          startDate: startOfMonth(now),
          endDate: endOfMonth(now),
          key: "selection",
        },
      ]);
      setTotalMonthlyBudget("");
      setCampaignType("");
      setSelectedGroup("");
      setPercentage("");
      setSelectedExistingBudget("");

      setRandom(Math.random());
    } catch (error) {
      console.error("Save monthly budget error:", error);
      if (!error.message.startsWith("API request failed")) {
        alert("An unexpected error occurred while saving the monthly budget.");
      }
    } finally {
      setSavingBudget(false);
      closeAll();
    }
  };

  useEffect(() => {
    if (
      budgetSourceType === "existing" &&
      percentage &&
      selectedExistingBudget
    ) {
      const selectedBudget = budgetData.find(
        (b) => b.id === selectedExistingBudget,
      );
      if (selectedBudget) {
        const newBudget =
          (parseFloat(percentage) / 100) *
          parseFloat(selectedBudget.total_budget);
        setCalculatedBudget(newBudget.toFixed(2));
      }
    } else {
      setCalculatedBudget(null);
    }
  }, [percentage, selectedExistingBudget, budgetData, budgetSourceType]);

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (!projectId) {
      console.warn("No project ID found.");
      setBudgetData([]);
      return;
    }
    localStorage.setItem("project_id", projectId);
    const loadBudgetData = async () => {
      await fetchMonthlyBudget();
    };

    loadBudgetData();
  }, [projectId, random, selectedBudgetType, groups, groups.length]);

  useEffect(() => {
    const { startDate: formStart, endDate: formEnd } =
      budgetCreationDateSelection[0];

    const filtered = budgetData.filter((budget) => {
      const budgetStart = new Date(budget.start_date);
      const budgetEnd = new Date(budget.end_date);

      const overlaps = budgetStart <= formEnd && budgetEnd >= formStart;
      if (!overlaps) {
        return false;
      }

      switch (selectedBudgetType) {
        case "overall":
          return budget.campaign_type === null && budget.group_id === null;
        case "campaign_type":
          return budget.campaign_type !== null;
        case "group_wise":
          return budget.group_id !== null;
        default:
          return true;
      }
    });

    setFilteredBudgetData(filtered);

    if (
      selectedExistingBudget &&
      !filtered.some((b) => b.id === selectedExistingBudget)
    ) {
      setSelectedExistingBudget("");
    }
  }, [budgetCreationDateSelection, budgetData, selectedBudgetType]);

  const fetchMonthlyBudget = async () => {
    const url = "https://api.confidanto.com/monthly-budget/get";
    const { startDate, endDate } = budgetDateSelection[0];

    const requestBody = {
      email: localStorage.getItem("email"),
      customer_id: localStorage.getItem("customer_id"),
      projectId: localStorage.getItem("project_id"),
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
    };

    setBudgetLoading(true);
    setBudgetError(false);
    await axios
      .post(url, requestBody)
      .then((res) => {
        if (res?.data.length > 0) {
          let structuredData = res.data.map((item) => {
            item.display_field =
              item.campaign_type ?? findGroupName(item.group_id) ?? "Overall";

            function findGroupName(groupId) {
              if (groupId == null) {
                return null;
              }
              const group = groups.find(
                (g) => String(g.group_id) === String(groupId),
              );
              return group?.group_name;
            }
            return item;
          });
          setBudgetData(structuredData);
        } else {
          setBudgetData([]);
        }
      })
      .catch((err) => {
        console.error("Error Fetching Monthly Budget: ", err);
        setBudgetError(true);
      })
      .finally((fin) => {
        setBudgetLoading(false);
      });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [timeFilter]);

  const displayableBudgets = useMemo(() => {
    if (!budgetData || budgetData.length === 0) return [];

    const now = new Date();

    return budgetData.filter((item) => {
      if (showOnlyActive && item.status !== "active") {
        return false;
      }

      const budgetStart = new Date(item.start_date);
      const end = endOfMonth(now);

      switch (timeFilter) {
        case "this_month": {
          const start = startOfMonth(now);
          return budgetStart >= start && budgetStart <= end;
        }
        case "last_3_month": {
          const threeMonthsAgo = subMonths(now, 3);
          return budgetStart >= threeMonthsAgo && budgetStart <= end;
        }
        case "last_6_month": {
          const sixMonthsAgo = subMonths(now, 6);
          return budgetStart >= sixMonthsAgo && budgetStart <= end;
        }
        case "1_year": {
          const oneYearAgo = subYears(now, 1);
          return budgetStart >= oneYearAgo && budgetStart <= end;
        }
        default:
          return true;
      }
    });
  }, [budgetData, timeFilter, showOnlyActive]);

  useEffect(() => {
    const now = new Date();
    let start;
    let end = endOfMonth(now);

    switch (timeFilter) {
      case "this_month":
        start = startOfMonth(now);
        break;
      case "last_3_month":
        start = subMonths(now, 3);
        break;
      case "last_6_month":
        start = subMonths(now, 6);
        break;
      case "1_year":
        start = subYears(now, 1);
        break;
      case "all_time":
      default:
        start = subYears(now, 10);
        break;
    }

    setBudgetDateSelection([
      {
        startDate: start,
        endDate: end,
        key: "selection",
      },
    ]);
  }, [timeFilter]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems =
    displayableBudgets.length > 0
      ? displayableBudgets.slice(indexOfFirstItem, indexOfLastItem)
      : [];

  const handleBudgetPeriodClick = (event) => {
    setDatePickerAnchorEl(event.currentTarget);
    setShowDatePicker((prev) => !prev);
  };

  const handleCloseDatePicker = () => {
    setShowDatePicker(false);
  };

  const handleDateRangeChange = (item) => {
    setBudgetCreationDateSelection([item.selection]);
  };

  const formatBudgetPeriodDisplay = () => {
    const { startDate, endDate } = budgetCreationDateSelection[0];
    if (!startDate || !endDate) return "Select Budget Period";
    if (format(startDate, "yyyy-MM-dd") === format(endDate, "yyyy-MM-dd")) {
      return format(startDate, "MMM dd, yyyy");
    }
    return `${format(startDate, "MMM dd, yyyy")} - ${format(
      endDate,
      "MMM dd, yyyy",
    )}`;
  };

  const handleApplyDateSelection = () => {
    setShowDatePicker(false);
  };

  const handleStartEdit = (index, item) => {
    setCurrentEditIndex(index);
    setEditedBudgetValue(item.total_budget);
    setEditedBudgetName(item.budget_name || "");
    setEditedGoogleSplit(
      item.google_ads_budget !== null ? item.google_ads_budget : 50,
    );

    setEditedDateSelection([
      {
        startDate: new Date(item.start_date),
        endDate: new Date(item.end_date),
        key: "selection",
      },
    ]);
  };

  const handleSaveBudget = (item) => {
    const { startDate, endDate } = editedDateSelection[0];

    const googleBudget = Number(editedGoogleSplit);
    const bingBudget = 100 - googleBudget;

    let payload = {
      id: item?.id,
      projectId: item.project_id,
      customer_id: item.customer_id,
      email: item.email,
      campaignType: item.campaign_type,
      group_id: item.group_id,
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
      total_budget: editedBudgetValue,
      budget_name: editedBudgetName,
      is_seperate_budget:
        item.display_field === "Overall" && areBothPlatformAvailable(),
    };

    if (areBothPlatformAvailable()) {
      payload = {
        ...payload,

        google_ads_budget: googleBudget,
        bing_ads_budget: bingBudget,
      };
    }

    axios
      .post("https://api.confidanto.com/monthly-budget/update", payload)
      .then((res) => {
        fetchMonthlyBudget();
      })
      .catch((err) => console.error("Monthly Budget Saving Error: ", err))
      .finally(() => {
        setCurrentEditIndex(null);
      });
  };

  const handleEditBudgetPeriodClick = (event) => {
    setEditDatePickerAnchorEl(event.currentTarget);
    setShowEditDatePicker(true);
  };

  const handleCloseEditDatePicker = () => {
    setShowEditDatePicker(false);
  };

  const handleEditDateRangeChange = (item) => {
    setEditedDateSelection([item.selection]);
  };

  return (
    <div className="bg-[#e9ecef] min-h-[calc(100vh-64px)] p-2 md:p-4 font-['Arial',sans-serif] mb-20">
      <div className="max-w-full mx-auto flex flex-col gap-6">
        <div className="bg-white shadow-md rounded-lg">
          <div className="p-5 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex flex-row items-center gap-2">
              <div className="text-blue-500 text-xl font-bold p-2">
                {budgetCurrency ?? "$"}
              </div>

              <h6 className="font-semibold text-xl">Monthly Budget</h6>
            </div>

            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4 w-full sm:w-auto">
              <div className="flex items-center">
                <span className="text-gray-700 text-sm font-medium mr-3">
                  Active Only
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showOnlyActive}
                    onChange={(e) => setShowOnlyActive(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div
                    className="w-10 h-5 bg-gray-300 rounded-full peer peer-checked:bg-blue-600 
              after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
              after:bg-white after:h-4 after:w-4 after:rounded-full after:transition-all 
              peer-checked:after:translate-x-5"
                  ></div>
                </label>
              </div>

              <FormControl size="small" className="w-40 bg-gray-50">
                <InputLabel id="time-filter-label" className="text-sm">
                  Period
                </InputLabel>
                <Select
                  labelId="time-filter-label"
                  value={timeFilter}
                  label="Period"
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className="text-sm"
                >
                  <MenuItem value="all_time">All Time</MenuItem>
                  <MenuItem value="this_month">This Month</MenuItem>
                  <MenuItem value="last_3_month">Last 3 Months</MenuItem>
                  <MenuItem value="last_6_month">Last 6 Months</MenuItem>
                  <MenuItem value="1_year">Last Year</MenuItem>
                </Select>
              </FormControl>

              <div className="flex items-center gap-3 text-sm font-medium">
                <button
                  onClick={() => {
                    setRandom(Math.random());
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 hover:text-blue-800 text-blue-600  transition"
                >
                  <FiRefreshCcw size={16} />
                  Refresh
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <div className="p-4 md:p-6 border-gray-200">
              {budgetLoading ? (
                <div className="flex justify-center items-center h-48 text-gray-600">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mr-3"></div>
                  <span>Loading Budgets...</span>
                </div>
              ) : budgetError || displayableBudgets.length <= 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                  <p className="italic text-base">
                    No Budget Data Found for selected period.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {currentItems.map((item, index) => {
                    const isEditing = currentEditIndex === index;
                    const { startDate, endDate } = editedDateSelection[0];

                    return (
                      <div
                        key={item.id}
                        className="relative bg-gray-50 border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-200 group flex flex-col"
                      >
                        {isEditing ? (
                          <>
                            <div className="absolute top-3 right-3 flex gap-3 z-10">
                              <button
                                onClick={() => handleSaveBudget(item)}
                                className="text-green-600 hover:text-green-800"
                                title="Save Budget"
                              >
                                <FaCheck size={16} />
                              </button>
                              <button
                                onClick={() => setCurrentEditIndex(null)}
                                className="text-red-600 hover:text-red-800"
                                title="Cancel Edit"
                              >
                                <ImCross size={14} />
                              </button>
                            </div>

                            <div className="mb-4 pr-12">
                              <span className="font-medium text-gray-700 text-xs mb-1 block">
                                Budget Name:
                              </span>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={editedBudgetName}
                                onChange={(e) =>
                                  setEditedBudgetName(e.target.value)
                                }
                                placeholder="Enter name"
                              />
                            </div>

                            <div className="space-y-3 text-sm flex-grow">
                              <p className="text-gray-500">
                                <span className="font-medium text-gray-700">
                                  Type/Group:
                                </span>{" "}
                                {item.display_field || "N/A"}
                              </p>
                              <div>
                                <span className="font-medium text-gray-700 text-sm">
                                  Budget Period:
                                </span>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  value={`${format(
                                    startDate,
                                    "MMM dd, yyyy",
                                  )} - ${format(endDate, "MMM dd, yyyy")}`}
                                  onClick={handleEditBudgetPeriodClick}
                                  className="mt-1"
                                  InputProps={{
                                    readOnly: true,
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <DateRangeIcon fontSize="small" />
                                      </InputAdornment>
                                    ),
                                    style: { fontSize: "0.875rem" },
                                  }}
                                />
                              </div>
                              <div>
                                <span className="font-medium text-gray-700 text-sm">
                                  Total Budget:
                                </span>
                                <TextField
                                  type="number"
                                  step="0.01"
                                  value={editedBudgetValue}
                                  onChange={(e) =>
                                    setEditedBudgetValue(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  className="mt-1"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span className="font-medium text-gray-600">
                                          {budgetCurrency || "$"}
                                        </span>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </div>

                              {item.display_field === "Overall" &&
                                areBothPlatformAvailable() && (
                                  <div className="pt-2 border-t border-dashed border-gray-200 mt-2">
                                    <span className="font-medium text-gray-700 text-sm block mb-1">
                                      Platform Split (%):
                                    </span>
                                    <div className="flex gap-2">
                                      <TextField
                                        label="Google"
                                        type="number"
                                        size="small"
                                        value={editedGoogleSplit}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (
                                            val === "" ||
                                            (Number(val) >= 0 &&
                                              Number(val) <= 100)
                                          ) {
                                            setEditedGoogleSplit(val);
                                          }
                                        }}
                                        className="w-1/2"
                                      />
                                      <TextField
                                        label="Bing"
                                        size="small"
                                        value={
                                          editedGoogleSplit !== ""
                                            ? 100 - Number(editedGoogleSplit)
                                            : ""
                                        }
                                        disabled
                                        className="w-1/2 bg-gray-50"
                                      />
                                    </div>
                                  </div>
                                )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="absolute top-3 right-3 flex gap-3  ">
                              <button
                                onClick={() => handleStartEdit(index, item)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Edit Budget"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => deleteMonthlyBudget(item)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete Budget"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 pr-5 break-words">
                              {item.budget_name || "—"}
                            </h3>
                            <div className="flex flex-col flex-grow space-y-4">
                              <div className="flex justify-between items-start">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider font-semibold">
                                    <Layers size={14} />
                                    <span>Type</span>
                                  </div>
                                  <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                                    {item.display_field || "Overall"}
                                  </span>

                                  <span
                                    className={`inline-block px-2 py-0.5 rounded-full capitalize text-xs text-white font-medium border ${item.status === "active" ? " bg-green-400" : "bg-red-400"}`}
                                  >
                                    {item.status?.toUpperCase()}
                                  </span>
                                </div>

                                <div className="text-right space-y-1">
                                  <div className="flex items-center justify-end gap-2 text-xs text-gray-500 uppercase tracking-wider font-semibold">
                                    <Calendar size={14} />
                                    <span>Period</span>
                                  </div>
                                  <p className="text-sm font-medium text-gray-700">
                                    {format(
                                      new Date(item.start_date),
                                      "MMM dd",
                                    )}{" "}
                                    -{" "}
                                    {format(
                                      new Date(item.end_date),
                                      "MMM dd, yyyy",
                                    )}
                                  </p>
                                </div>
                              </div>

                              <hr className="border-gray-100" />

                              <div>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                  <Wallet size={16} />
                                  <span>Total Budget</span>
                                </div>
                                <p className="text-2xl font-bold text-gray-900 tracking-tight">
                                  <span className="text-lg text-gray-500 font-medium mr-1">
                                    {budgetCurrency || "$"}
                                  </span>
                                  {displayNumericValue(item.total_budget)}
                                </p>
                              </div>

                              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 flex flex-col justify-center h-[74px]">
                                {item.google_ads_budget != null ||
                                item.bing_ads_budget != null ? (
                                  <>
                                    <div className="flex items-center justify-between text-xs font-medium mb-2">
                                      <div className="flex items-center gap-1.5">
                                        <PieChart
                                          size={14}
                                          className="text-gray-400"
                                        />
                                        <span className="text-gray-600">
                                          Platform Split
                                        </span>
                                      </div>
                                      <div className="flex gap-3">
                                        <span className="text-blue-600">
                                          Google: {item.google_ads_budget ?? 0}%
                                        </span>
                                        <span className="text-emerald-600">
                                          Bing: {item.bing_ads_budget ?? 0}%
                                        </span>
                                      </div>
                                    </div>

                                    <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden flex">
                                      <div
                                        className="h-full bg-blue-500"
                                        style={{
                                          width: `${item.google_ads_budget}%`,
                                        }}
                                      ></div>
                                      <div
                                        className="h-full bg-emerald-500"
                                        style={{
                                          width: `${item.bing_ads_budget}%`,
                                        }}
                                      ></div>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex items-center justify-between opacity-50">
                                    <div className="flex items-center gap-1.5 text-xs font-medium">
                                      <PieChart
                                        size={14}
                                        className="text-gray-400"
                                      />
                                      <span className="text-gray-600">
                                        Platform Split
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                      <AlertCircle size={12} />
                                      <span>Not set</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <Popper
                open={showEditDatePicker}
                anchorEl={editDatePickerAnchorEl}
                placement="bottom-start"
                transition
                modifiers={[
                  { name: "offset", options: { offset: [0, 8] } },
                  { name: "flip", enabled: true },
                ]}
                className="z-[1300]"
              >
                {({ TransitionProps }) => (
                  <ClickAwayListener onClickAway={handleCloseEditDatePicker}>
                    <Fade {...TransitionProps} timeout={200}>
                      <div className="bg-white shadow-lg overflow-hidden rounded-md border">
                        <DateRangePicker
                          onChange={handleEditDateRangeChange}
                          showSelectionPreview={true}
                          moveRangeOnFirstSelection={false}
                          months={1}
                          ranges={editedDateSelection}
                          direction="horizontal"
                        />
                        <div className="flex justify-end p-3 border-t border-gray-200 bg-gray-50">
                          <Button
                            onClick={handleCloseEditDatePicker}
                            variant="contained"
                            color="primary"
                            size="small"
                            className="normal-case bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-xs"
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </Fade>
                  </ClickAwayListener>
                )}
              </Popper>
            </div>

            <div className="p-4 md:p-6 w-full flex flex-row items-center justify-start">
              <Button
                variant="contained"
                onClick={() => {
                  setSelectedBudgetType("");
                  setShowBudgetForm(!showBudgetForm);
                }}
                disabled={!projectId}
                className={`${saveButtonBaseClasses} bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-70`}
              >
                {showBudgetForm ? "Close" : "Add Budget"}
              </Button>
            </div>

            {showBudgetForm && !selectedBudgetType && (
              <div className="p-4 md:p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Select Budget Type
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {budgetTypeOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        if (option.value === "overall") {
                          setBudgetSourceType("new");
                        }
                        setSelectedBudgetType(option.value);
                      }}
                      className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{option.icon}</span>
                        <div>
                          <h4 className="font-medium">{option.title}</h4>
                          <p className="text-sm text-gray-600">
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showBudgetForm && selectedBudgetType && (
              <form noValidate autoComplete="off" className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {
                      budgetTypeOptions.find(
                        (opt) => opt.value === selectedBudgetType,
                      )?.title
                    }
                  </h3>
                  <Button
                    variant="text"
                    onClick={() => setSelectedBudgetType("")}
                    className="text-gray-600"
                  >
                    Back
                  </Button>
                </div>
                <div className="grid grid-cols-12 gap-5">
                  {selectedBudgetType === "campaign_type" && (
                    <>
                      <div className="col-span-12 md:col-span-6">
                        <label className="block text-xs mb-1 ml-1 text-gray-500">
                          Select Engine
                        </label>
                        <TextField
                          select
                          fullWidth
                          variant="outlined"
                          value={selectedEngine}
                          onChange={(e) => setSelectedEngine(e.target.value)}
                          className={textFieldClasses}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ManageSearch className={iconClasses} />
                              </InputAdornment>
                            ),
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Engine Type</em>
                          </MenuItem>
                          {ENGINES.filter((opt) => {
                            if (opt.value === "google_ads")
                              return connectedPlatforms.google;
                            if (opt.value === "Bing_ads")
                              return connectedPlatforms.microsoft;
                            return true;
                          }).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <label className="block text-xs mb-1 ml-1 text-gray-500">
                          Campaign Type
                        </label>
                        <TextField
                          select
                          fullWidth
                          variant="outlined"
                          value={campaignType}
                          onChange={(e) => setCampaignType(e.target.value)}
                          className={textFieldClasses}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CampaignTypeIcon className={iconClasses} />
                              </InputAdornment>
                            ),
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Campaign Type</em>
                          </MenuItem>
                          {campaignTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </>
                  )}
                  {selectedBudgetType === "group_wise" && (
                    <div className="col-span-12 md:col-span-6 ">
                      <label className="block text-xs mb-1 ml-1 text-gray-500">
                        Group wise
                      </label>
                      <FormControl fullWidth>
                        <InputLabel>Group Names</InputLabel>
                        <Select
                          value={selectedGroup}
                          onChange={(e) => setSelectedGroup(e.target.value)}
                          input={<OutlinedInput label="Group Names" />}
                        >
                          {groups.map((group) => (
                            <MenuItem
                              key={group.group_id}
                              value={group.group_name}
                            >
                              <ListItemText primary={group.group_name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  )}

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-xs mb-1 ml-1 text-gray-500">
                      Budget Name
                    </label>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="text"
                      value={budgetName}
                      onChange={(e) => setBudgetName(e.target.value)}
                      className={textFieldClasses}
                    />
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-xs mb-1 ml-1 text-gray-500">
                      Budget Period
                    </label>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formatBudgetPeriodDisplay()}
                      onClick={handleBudgetPeriodClick}
                      inputRef={budgetPeriodFieldRef}
                      className={textFieldClasses}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <DateRangeIcon className={iconClasses} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Popper
                      open={showDatePicker}
                      anchorEl={datePickerAnchorEl}
                      placement="top-start"
                      transition
                      modifiers={[
                        {
                          name: "offset",
                          options: {
                            offset: [0, 8],
                          },
                        },
                        {
                          name: "flip",
                          enabled: false,
                        },
                      ]}
                      className="z-[1300]"
                    >
                      {({ TransitionProps }) => (
                        <ClickAwayListener onClickAway={handleCloseDatePicker}>
                          <Fade {...TransitionProps} timeout={200}>
                            <div className="bg-white shadow-lg overflow-hidden rounded-md border">
                              <DateRangePicker
                                onChange={handleDateRangeChange}
                                showSelectionPreview={true}
                                moveRangeOnFirstSelection={false}
                                months={1}
                                minDate={
                                  new Date(
                                    new Date().getFullYear(),
                                    new Date().getMonth(),
                                    1,
                                  )
                                }
                                ranges={budgetCreationDateSelection}
                                direction="horizontal"
                              />
                              <div className="flex justify-end p-3 border-t border-gray-200 bg-gray-50">
                                <Button
                                  onClick={handleApplyDateSelection}
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  className="normal-case bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-xs"
                                >
                                  Apply
                                </Button>
                              </div>
                            </div>
                          </Fade>
                        </ClickAwayListener>
                      )}
                    </Popper>
                  </div>

                  {selectedBudgetType !== "overall" && (
                    <div className="col-span-12 md:col-span-6">
                      <label className="block text-xs mb-1 ml-1 text-gray-500">
                        Budget Type
                      </label>
                      <FormControl fullWidth>
                        <Select
                          value={budgetSourceType}
                          onChange={(e) => setBudgetSourceType(e.target.value)}
                          input={<OutlinedInput />}
                        >
                          <MenuItem value="new">New</MenuItem>
                          <MenuItem value="existing">Existing</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  )}

                  {budgetSourceType === "new" ||
                  selectedBudgetType === "overall" ? (
                    <>
                      <div className="col-span-12 md:col-span-6">
                        <label className="block text-xs mb-1 ml-1 text-gray-500">
                          Total Monthly Budget
                        </label>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="number"
                          value={totalMonthlyBudget}
                          onChange={(e) =>
                            setTotalMonthlyBudget(e.target.value)
                          }
                          className={textFieldClasses}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <span className="font-medium text-gray-800 mr-1">
                                  {budgetCurrency || budgetCurrency}
                                </span>
                              </InputAdornment>
                            ),
                            inputProps: { min: 0, step: "0.01" },
                          }}
                          helperText={`Budget will be in ${budgetCurrency}`}
                        />
                      </div>

                      {selectedBudgetType === "overall" &&
                        connectedPlatforms.google &&
                        connectedPlatforms.microsoft && (
                          <>
                            <div className="col-span-12 md:col-span-3">
                              <label className="block text-xs mb-1 ml-1 text-gray-500">
                                Separate Budget
                              </label>
                              <FormControl fullWidth>
                                <Select
                                  value={seperateBudgeType}
                                  onChange={(e) =>
                                    setSeperateBudgetType(e.target.value)
                                  }
                                  input={<OutlinedInput />}
                                >
                                  <MenuItem value="none">None</MenuItem>
                                  <MenuItem value="google_ads">
                                    Google Ads
                                  </MenuItem>
                                  <MenuItem value="bing_ads">Bing Ads</MenuItem>
                                </Select>
                                <FormHelperText>Optional</FormHelperText>
                              </FormControl>
                            </div>

                            {seperateBudgeType !== "none" && (
                              <div className="col-span-12 md:col-span-3">
                                <label className="block text-xs mb-1 ml-1 text-gray-500">
                                  Budget For{" "}
                                  {seperateBudgeType === "google_ads"
                                    ? "Google Ads"
                                    : "Bing Ads"}
                                </label>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  type="number"
                                  value={seperateBudget}
                                  onChange={(e) => {
                                    const val = Number(e.target.value);
                                    if (val <= 100 && val >= 0) {
                                      setSeperateBudget(e.target.value);
                                    }
                                  }}
                                  className={textFieldClasses}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        %
                                      </InputAdornment>
                                    ),
                                  }}
                                  helperText={
                                    seperateBudget
                                      ? `${
                                          seperateBudgeType === "google_ads"
                                            ? "Bing Ads"
                                            : "Google Ads"
                                        } budget would be ${
                                          100 - (Number(seperateBudget) || 0)
                                        }%`
                                      : ""
                                  }
                                />
                              </div>
                            )}
                          </>
                        )}
                    </>
                  ) : (
                    <>
                      <div className="col-span-12 md:col-span-3">
                        <label className="block text-xs mb-1 ml-1 text-gray-500">
                          Percentage
                        </label>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="number"
                          value={percentage}
                          onChange={(e) => setPercentage(e.target.value)}
                          className={textFieldClasses}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                        />
                      </div>
                      <div className="col-span-12 md:col-span-3">
                        <label className="block text-xs mb-1 ml-1 text-gray-500">
                          {`Existing Budget for ${format(
                            budgetCreationDateSelection[0].startDate,
                            "MMMM",
                          )}`}
                        </label>
                        <FormControl fullWidth>
                          <Select
                            value={selectedExistingBudget}
                            onChange={(e) =>
                              setSelectedExistingBudget(e.target.value)
                            }
                            input={<OutlinedInput />}
                          >
                            {filteredBudgetData.map((budget) => (
                              <MenuItem key={budget.id} value={budget.id}>
                                <ListItemText
                                  primary={`${budget.budget_name} (${budgetCurrency}${budget.total_budget})`}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </>
                  )}
                </div>

                {calculatedBudget !== null && (
                  <div className="pt-4">
                    <p className="text-sm text-gray-600 mt-1 ml-1">
                      Calculated Budget:{" "}
                      <span className="font-semibold text-green-700">
                        {budgetCurrency || budgetCurrency}
                        {calculatedBudget}
                      </span>
                    </p>
                  </div>
                )}

                {seperateBudgeType !== "none" && seperateBudget !== "" && (
                  <div className="pt-4">
                    <p className="text-sm text-gray-600 mt-1 ml-1">
                      {seperateBudgeType === "google_ads"
                        ? "Google Ads"
                        : "Bing Ads"}{" "}
                      Budget:{" "}
                      <span className="font-semibold text-green-700">
                        {budgetCurrency || budgetCurrency}
                        {(
                          (Number(seperateBudget) *
                            Number(totalMonthlyBudget)) /
                          100
                        ).toFixed(2)}
                      </span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1 ml-1">
                      {seperateBudgeType !== "google_ads"
                        ? "Google Ads"
                        : "Bing Ads"}{" "}
                      Budget:{" "}
                      <span className="font-semibold text-green-700">
                        {budgetCurrency || budgetCurrency}
                        {(
                          ((100 - Number(seperateBudget)) *
                            Number(totalMonthlyBudget)) /
                          100
                        ).toFixed(2)}
                      </span>
                    </p>
                  </div>
                )}
                <div className="pt-4 flex justify-start">
                  <Button
                    variant="contained"
                    onClick={handleSaveMonthlyBudget}
                    disabled={
                      savingBudget ||
                      !projectId ||
                      (selectedBudgetType === "campaign_type" && !campaignType)
                    }
                    className={`${saveButtonBaseClasses} bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-70`}
                  >
                    {savingBudget ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Save Budget"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
